// Única fuente de verdad del "Sugerido de pago" (unificación jul-2026).
// REGLA: el frontend NO calcula mora. Todos los importes se LEEN de valores
// sellados/persistidos por el backend (pagos_engine, políticas selladas del crédito).
// BLOQUE 1 (jul-2026): incluye el IVA del moratorio (Art. 15-X LIVA) sellado por el motor.
// BLOQUE 2 (jul-2026): incluye los GC ACTIVOS (no solo diferidos) — el motor los cobra en FASE 0.
export const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

export const GC_ETIQUETAS = ["Gastos de cobranza", "Cargo por pago tardío", "Comisión por atraso"];

// Renglón de IVA acorde a la etiqueta configurable del cargo GC.
export const ivaEtiquetaGc = (etq) =>
  etq === "Cargo por pago tardío" ? "IVA del cargo por pago tardío"
    : etq === "Comisión por atraso" ? "IVA de la comisión por atraso"
      : "IVA de gastos de cobranza";

// credito: crédito completo (o shape con totales de diferidos / proxima_cuota_monto).
// cuota: objeto sellado de tabla_amortizacion (o null → usa credito.proxima_cuota_monto).
export function sugeridoDePago(credito, cuota) {
  const c = credito || {};
  const cuotaCap = cuota ? Math.max(0, round2(Number(cuota.capital || 0) - Number(cuota.pagado_capital || 0))) : 0;
  const cuotaInt = cuota ? Math.max(0, round2(Number(cuota.interes || 0) - Number(cuota.pagado_interes || 0))) : 0;
  const cuotaIva = cuota ? Math.max(0, round2(Number(cuota.iva_interes || 0) - Number(cuota.pagado_iva || 0))) : 0;
  // Comisión de apertura FINANCIADA en la cuota (jul-2026): 0 en créditos sin comisión en tabla
  const cuotaComision = cuota
    ? Math.max(0, round2(Number(cuota.comision_cuota || 0) + Number(cuota.iva_comision_cuota || 0)
        - Number(cuota.pagado_comision || 0) - Number(cuota.pagado_iva_comision || 0)))
    : 0;
  const cuotaPend = cuota ? round2(cuotaCap + cuotaInt + cuotaIva) : round2(c.proxima_cuota_monto);
  // Mora corriente = pendiente persistido por el motor (mora_acumulada − pagado_moratorio).
  // Las cuotas congeladas no exigen mora aquí: su deuda vive en los diferidos.
  const moraCorriente = cuota && !cuota.mora_congelada
    ? Math.max(0, round2(Number(cuota.mora_acumulada || 0) - Number(cuota.pagado_moratorio || 0)))
    : 0;
  // IVA del moratorio corriente — sellado por el motor (0 en créditos exentos).
  const ivaMoraCorriente = cuota && !cuota.mora_congelada
    ? Math.max(0, round2(Number(cuota.iva_mora_acumulada || 0) - Number(cuota.pagado_iva_moratorio || 0)))
    : 0;
  const moraDiasCorriente = moraCorriente > 0 ? Number(cuota.dias_atraso || 0) : 0;
  const difMora = round2(c.total_moratorios_diferidos_pendientes);
  // IVA de la mora diferida: total persistido; fallback = suma del detalle por cuota.
  const difMoraIva = c.total_iva_moratorios_diferidos_pendientes != null
    ? round2(c.total_iva_moratorios_diferidos_pendientes)
    : round2((c.moratorios_diferidos || []).reduce((s, m) => s + Number(m.iva_pendiente || 0), 0));
  const difGc = round2(c.total_gastos_cobranza_diferidos_pendientes);
  // GC ACTIVOS (cargos aún no diferidos): total pendiente − diferidos.
  const gcActivos = Math.max(0, round2(Number(c.total_gastos_cobranza_pendientes || 0) - difGc));
  // Desglose GC activos en monto + IVA (leído de los cargos SELLADOS; los GC siempre
  // causan IVA — no son intereses, la exención del Art. 15-X no los cubre). Si el shape
  // no trae cargos_cobranza → null y la UI cae al renglón combinado. El desglose SIEMPRE
  // suma exactamente gcActivos (el IVA absorbe el redondeo).
  let gcDesglose = null;
  if (gcActivos > 0 && Array.isArray(c.cargos_cobranza)) {
    const monto = round2(c.cargos_cobranza
      .filter((x) => !x.revertido && !x.diferido)
      .reduce((s, x) => s + Math.max(0, round2(Number(x.monto || 0) - Number(x.pagado_monto || 0))), 0));
    const m = Math.min(monto, gcActivos);
    gcDesglose = { monto: m, iva: Math.max(0, round2(gcActivos - m)) };
  }
  // Etiqueta configurable del cargo (jul-2026), sellada por crédito.
  const gcEtiqueta = GC_ETIQUETAS.includes(c.gc_etiqueta) ? c.gc_etiqueta : "Gastos de cobranza";
  const difDetalle = (c.moratorios_diferidos || []).filter((m) => !m.pagado)
    .map((m) => ({ ...m, pendiente: round2(Number(m.monto_congelado || 0) - Number(m.pagado_monto || 0)) }));
  const total = round2(cuotaPend + cuotaComision + moraCorriente + ivaMoraCorriente + difMora + difMoraIva + difGc + gcActivos);
  return {
    cuota: cuotaPend, cuotaCap, cuotaInt, cuotaIva, cuotaComision,
    moraCorriente, ivaMoraCorriente, moraDiasCorriente,
    difMora, difMoraIva, difGc, gcActivos, gcDesglose, gcEtiqueta, difDetalle, total,
    proximaCuotaNo: cuota?.no ?? c.proxima_cuota_no ?? null,
    fecha: cuota?.fecha_pago || null,
    esParcial: !!cuota && cuotaPend < Number(cuota.pago || 0) - 0.005,
  };
}

// feb-2026 iter123 (caso ZINALI CR-2026-005) — Desglose ORDENADO según prelación
// exacta del motor sellado (`pagos_engine.aplicar_pago`). El operador ve, ANTES de
// aplicar, en qué orden se cobrará su pago. Es la fuente única de verdad para el
// modal de pago; la tabla de amortización usa `cuota.total_exigible` que viene
// enriquecido por backend.
//
// Orden que devuelve (mismo del motor):
//   1) mora congelada diferida
//   2) IVA mora diferida
//   3) GC diferidos
//   4) IVA GC diferidos
//   5) GC activos
//   6) IVA GC activos
//   7) comisión de apertura de la próxima cuota exigible
//   8) IVA comisión
//   9) interés
//  10) IVA interés
//  11) capital
//  12) mora corriente
//  13) IVA mora corriente
export function desgloseOrdenado(credito, cuota) {
  const c = credito || {};
  const gcEtiqueta = GC_ETIQUETAS.includes(c.gc_etiqueta) ? c.gc_etiqueta : "Gastos de cobranza";
  const ivaGcLbl = ivaEtiquetaGc(gcEtiqueta);
  const items = [];
  const push = (tipo, label, monto, cuotaNo) => {
    const m = round2(monto);
    if (m > 0.005) items.push({ tipo, label, monto: m, cuota_no: cuotaNo ?? null });
  };
  // FASE 0 — nivel crédito (motor: `_pagar_diferidos` con `incluir_gc_activos=True`)
  const difMora = round2(c.total_moratorios_diferidos_pendientes);
  const difMoraIva = c.total_iva_moratorios_diferidos_pendientes != null
    ? round2(c.total_iva_moratorios_diferidos_pendientes)
    : round2((c.moratorios_diferidos || []).reduce((s, m) => s + Number(m.iva_pendiente || 0), 0));
  push("mora_diferida", "Mora congelada diferida", difMora);
  push("iva_mora_diferida", "IVA de la mora diferida", difMoraIva);
  const difGc = round2(c.total_gastos_cobranza_diferidos_pendientes);
  const cargos = Array.isArray(c.cargos_cobranza) ? c.cargos_cobranza : [];
  // GC diferidos: monto + IVA por separado usando cargos_cobranza (si viene).
  let difGcMonto = 0, difGcIva = 0;
  cargos.filter((x) => !x.revertido && x.diferido).forEach((x) => {
    difGcMonto += Math.max(0, round2(Number(x.monto || 0) - Number(x.pagado_monto || 0)));
    difGcIva += Math.max(0, round2(Number(x.iva || 0) - Number(x.pagado_iva || 0)));
  });
  // Si por alguna razón no vino el desglose, cae al total combinado.
  if (difGcMonto + difGcIva <= 0.005 && difGc > 0) {
    push("gc_diferido", `${gcEtiqueta} diferidos (+ IVA)`, difGc);
  } else {
    push("gc_diferido", `${gcEtiqueta} diferidos`, difGcMonto);
    push("iva_gc_diferido", `IVA de ${gcEtiqueta.toLowerCase()} diferidos`, difGcIva);
  }
  // GC activos — desglose monto + IVA (motor los cobra en FASE 0 con `incluir_gc_activos=True`)
  let actMonto = 0, actIva = 0;
  cargos.filter((x) => !x.revertido && !x.diferido).forEach((x) => {
    actMonto += Math.max(0, round2(Number(x.monto || 0) - Number(x.pagado_monto || 0)));
    actIva += Math.max(0, round2(Number(x.iva || 0) - Number(x.pagado_iva || 0)));
  });
  if (actMonto + actIva > 0.005) {
    push("gc_activo", `${gcEtiqueta} (activos)`, actMonto);
    push("iva_gc_activo", ivaGcLbl, actIva);
  }
  // FASE 1 — por cuota (motor: comisión → IVA → interés → IVA → capital)
  if (cuota) {
    const no = cuota.no;
    const rem = {
      capital: Math.max(0, round2(Number(cuota.capital || 0) - Number(cuota.pagado_capital || 0))),
      interes: Math.max(0, round2(Number(cuota.interes || 0) - Number(cuota.pagado_interes || 0))),
      iva_interes: Math.max(0, round2(Number(cuota.iva_interes || 0) - Number(cuota.pagado_iva || 0))),
      comision: Math.max(0, round2(Number(cuota.comision_cuota || 0) - Number(cuota.pagado_comision || 0))),
      iva_comision: Math.max(0, round2(Number(cuota.iva_comision_cuota || 0) - Number(cuota.pagado_iva_comision || 0))),
      mora_corriente: cuota.mora_congelada ? 0 : Math.max(0, round2(Number(cuota.mora_acumulada || 0) - Number(cuota.pagado_moratorio || 0))),
      iva_mora_corriente: cuota.mora_congelada ? 0 : Math.max(0, round2(Number(cuota.iva_mora_acumulada || 0) - Number(cuota.pagado_iva_moratorio || 0))),
    };
    push("comision", `Comisión de apertura — cuota #${no}`, rem.comision, no);
    push("iva_comision", `IVA comisión — cuota #${no}`, rem.iva_comision, no);
    push("interes", `Interés ordinario — cuota #${no}`, rem.interes, no);
    push("iva_interes", `IVA interés — cuota #${no}`, rem.iva_interes, no);
    push("capital", `Capital — cuota #${no}`, rem.capital, no);
    // FASE 1.5 — mora corriente (post-liquidar cuota vencida)
    const diasAtraso = cuota.dias_atraso || 0;
    const lblMora = `Intereses moratorios — cuota #${no}` + (diasAtraso ? ` (${diasAtraso} días de atraso)` : "");
    push("mora_corriente", lblMora, rem.mora_corriente, no);
    push("iva_mora_corriente", "IVA intereses moratorios", rem.iva_mora_corriente, no);
  }
  const total = round2(items.reduce((s, it) => s + it.monto, 0));
  return { items, total };
}
